class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 200);

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);

    // Reset infinite scroll on filter change
    if (window.InfiniteScrollHelper && typeof window.InfiniteScrollHelper.reset === 'function') {
      window.InfiniteScrollHelper.reset();
    }
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  // CWV: CLS | Why: full grid innerHTML swap without reserved height | Advantage: stable viewport during filter fetch
  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const prevHeight = container.offsetHeight;
    if (prevHeight > 0) {
      container.style.minHeight = prevHeight + 'px';
    }

    container.innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    container.querySelectorAll('.scroll-trigger')
      .forEach((element) => {
        element.classList.add('scroll-trigger--cancel');
      });

    requestAnimationFrame(() => {
      container.style.minHeight = '';
    });

    if (window.InfiniteScrollHelper && typeof window.InfiniteScrollHelper.reset === 'function') {
      window.InfiniteScrollHelper.reset();
    }
  }

  static renderProductCount(html) {
    const count = new DOMParser().parseFromString(html, 'text/html').getElementById('ProductCount').innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    container.innerHTML = count;
    container.classList.remove('loading');
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
      if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
        currentElement.remove();
      }
    });

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    facetsToRender.forEach((elementToRender, index) => {
      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
      } else {
        if (index > 0) {
          const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
          if (elementToRender.className === previousElementClassName) {
            document.getElementById(previousElementId).after(elementToRender);
            return;
          }
        }

        if (elementToRender.parentElement) {
          document.querySelector(`#${elementToRender.parentElement.id} .js-filter`).before(elementToRender);
        }
      }
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender) {
      const closestJSFilterID = event.target.closest('.js-filter').id;
      if (closestJSFilterID) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      if (!activeFacetsElement) return;
      document.querySelector(selector).innerHTML = activeFacetsElement.innerHTML;
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      document.querySelector(selector).innerHTML = html.querySelector(selector).innerHTML;
    });

    document.getElementById('FacetFiltersFormMobile').closest('menu-drawer').bindEvents();
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary');
    const sourceSummary = source.querySelector('.facets__summary');
    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list');
    const sourceFacetsList = source.querySelector('.mobile-facets__list');
    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    return [
      {
        section: document.getElementById('product-grid').dataset.id,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    if (event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(event.target.closest('form'));
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = event.target.closest('form').id === 'FacetFiltersFormMobile';
      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      this.onSubmitForm(forms.join('&'), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();


// CWV: CLS,INP | Why: loadNextPage ran on every scroll; undefined InfiniteScrollHelper; preloaded all imgs | Advantage: gated fetch + scoped image work
(function () {
  let loader = document.getElementById("loaders");
  let productGrid = document.getElementById("product-grid");
  let paginationContainer = document.getElementById("pagination");

  let currentPage = 1;
  let totalPages = getTotalPages();
  let loading = false;

  function getTotalPages() {
    if (!paginationContainer) return 1;

    let lastPageLink = paginationContainer.querySelector("a[href*='page=']:last-of-type");
    if (lastPageLink) {
      let url = new URL(lastPageLink.href);
      return parseInt(url.searchParams.get("page")) || 1;
    }

    return 1;
  }

  function buildNextPageURL() {
    if (!productGrid) return null;
    const sectionId = productGrid.dataset.id;
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set("page", currentPage + 1);
    return `${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`;
  }

  function loadNextPage() {
    if (!productGrid || loading || currentPage >= totalPages) return;
    const url = buildNextPageURL();
    if (!url) return;
    loading = true;
    if (loader) loader.style.display = "block";

    fetch(url)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        let newProducts = doc.querySelector("#product-grid");
        let newPagination = doc.querySelector("#pagination");

        if (newProducts) {
          const beforeCount = productGrid.children.length;
          productGrid.insertAdjacentHTML("beforeend", newProducts.innerHTML);

          // Preload only newly appended images
          const imgs = productGrid.querySelectorAll("img");
          for (let i = beforeCount; i < imgs.length; i++) {
            const img = imgs[i];
            if (img && !img.complete) {
              const preImg = new Image();
              preImg.src = img.dataset.src || img.src;
            }
          }
        }

        if (newPagination && paginationContainer) {
          paginationContainer.innerHTML = newPagination.innerHTML;
        }

        paginationContainer = document.getElementById("pagination");
        totalPages = getTotalPages();

        currentPage++;
        loading = false;
        if (loader) loader.style.display = "none";
      })
      .catch(() => {
        loading = false;
        if (loader) loader.style.display = "none";
      });
  }

  function onScroll() {
    if (loading || !productGrid) return;

    const scrollPosition = window.scrollY + window.innerHeight;
    const pageHeight = document.documentElement.scrollHeight;

    if (scrollPosition >= pageHeight - 500) {
      loadNextPage();
    }
  }

  function initInfiniteLoader() {
    productGrid = document.getElementById("product-grid");
    paginationContainer = document.getElementById("pagination");
    loader = document.getElementById("loaders");
    currentPage = 1;
    totalPages = getTotalPages();
    loading = false;
    window.removeEventListener("scroll", onScroll);
    window.addEventListener("scroll", onScroll, { passive: true });
  }

  window.InfiniteScrollHelper = {
    reset: initInfiniteLoader
  };

  const inStockCheckbox = document.getElementById("Filter-filter.v.availability-1");
  const outOfStockCheckbox = document.getElementById("Filter-filter.v.availability-2");
  const priceMinInput = document.getElementById("Filter-Price-GTE");
  const priceMaxInput = document.getElementById("Filter-Price-LTE");
  const sortSelect = document.getElementById("SortBy");

  let filterDebounceTimer = null;
  function applyFilters() {
    if (!productGrid) return;
    const searchParams = new URLSearchParams();

    searchParams.set("page", "1");

    if (inStockCheckbox?.checked) {
      searchParams.append("filter.v.availability", "1");
    }
    if (outOfStockCheckbox?.checked) {
      searchParams.append("filter.v.availability", "0");
    }

    if (priceMinInput?.value && priceMaxInput?.value) {
      searchParams.set("filter.v.price.gte", priceMinInput.value);
      searchParams.set("filter.v.price.lte", priceMaxInput.value);
    }

    if (sortSelect?.value) {
      searchParams.set("sort_by", sortSelect.value);
    }

    const sectionId = productGrid.dataset.id;
    if (loader) loader.style.display = "block";

    const gridContainer = document.getElementById("ProductGridContainer");
    if (gridContainer) {
      gridContainer.style.minHeight = gridContainer.offsetHeight + "px";
    }

    fetch(`${window.location.pathname}?section_id=${sectionId}&${searchParams.toString()}`)
      .then(res => res.text())
      .then(html => {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        let newProducts = doc.querySelector("#product-grid");
        let newPagination = doc.querySelector("#pagination");

        if (newProducts) productGrid.innerHTML = newProducts.innerHTML;
        if (newPagination && paginationContainer) paginationContainer.innerHTML = newPagination.innerHTML;

        productGrid = document.getElementById("product-grid");
        paginationContainer = document.getElementById("pagination");
        totalPages = getTotalPages();

        window.history.replaceState({}, "", `${window.location.pathname}?${searchParams.toString()}`);

        initInfiniteLoader();
        if (loader) loader.style.display = "none";
        if (gridContainer) {
          requestAnimationFrame(() => { gridContainer.style.minHeight = ""; });
        }
      })
      .catch(() => {
        if (loader) loader.style.display = "none";
      });
  }

  function debouncedApplyFilters() {
    clearTimeout(filterDebounceTimer);
    filterDebounceTimer = setTimeout(applyFilters, 200);
  }

  [inStockCheckbox, outOfStockCheckbox].forEach(cb =>
    cb?.addEventListener("change", debouncedApplyFilters)
  );

  [priceMinInput, priceMaxInput].forEach(inp =>
    inp?.addEventListener("change", () => {
      if (priceMinInput?.value && priceMaxInput?.value) {
        debouncedApplyFilters();
      }
    })
  );

  sortSelect?.addEventListener("change", debouncedApplyFilters);

  document.addEventListener("shopify:section:load", () => {
    productGrid = document.getElementById("product-grid");
    paginationContainer = document.getElementById("pagination");
    initInfiniteLoader();
  });

  if (productGrid) {
    initInfiniteLoader();
  }
})();

